import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SectionLabel,
  Select,
} from '@wundo/ui';
import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import { attemptTrend, previousScoredAttempt } from './attemptTrend.ts';
import { PracticeReport } from './PracticeReport.tsx';
import {
  formatAttemptLabel,
  formatAttemptOption,
  formatTrend,
} from './practiceFormat.ts';
import { fetchRoleplayAttempts } from '@/lib/roleplays.ts';
import { m } from '@/paraglide/messages.js';

interface PracticeFeedbackBodyProps {
  card: RoleplayCard;
  starting: boolean;
  onClose: () => void;
  onPractiseAgain: () => void;
}

/**
 * The feedback itself, for whichever attempt is selected.
 *
 * Attempts without a debrief are left out entirely: a session that was never
 * finished has nothing to say, and an option that opens onto an empty panel is
 * worse than no option at all.
 */
function PracticeFeedbackBody({
  card,
  starting,
  onClose,
  onPractiseAgain,
}: PracticeFeedbackBodyProps) {
  const {
    data: attempts = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ['roleplayAttempts', card.id],
    queryFn: () => fetchRoleplayAttempts(card.id),
  });

  const scored = useMemo(
    () => attempts.filter((attempt) => attempt.debrief !== null),
    [attempts],
  );

  const id = useId();

  /* Null means "whichever is newest", so a fresh debrief opens on itself. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIndex = Math.max(
    0,
    scored.findIndex((attempt) => attempt.id === selectedId),
  );
  const selected = scored[selectedIndex];
  const previous = previousScoredAttempt(scored, selectedIndex);

  const trend = formatTrend(
    attemptTrend(scored, selectedIndex),
    previous === undefined
      ? null
      : formatAttemptLabel(previous.completed_at ?? previous.created_at),
  );

  return (
    <>
      {scored.length > 0 && (
        <div className='flex flex-wrap items-center gap-2 border-b border-line px-6.5 py-4'>
          <SectionLabel id={`${id}-attempt`}>{m.practice_attempt()}</SectionLabel>
          <Select
            aria-labelledby={`${id}-attempt`}
            value={selected?.id ?? ''}
            options={scored.map((attempt, index) => ({
              value: attempt.id,
              label: formatAttemptOption(
                scored.length - index,
                attempt.completed_at ?? attempt.created_at,
                index === 0,
              ),
            }))}
            onChange={(event) => setSelectedId(event.target.value)}
          />
          {trend !== null && (
            <span className='ml-auto text-base font-medium text-ink-secondary'>
              {trend}
            </span>
          )}
        </div>
      )}

      {/*
        The report scrolls inside itself, the open tab only, so the buttons
        under it stay on screen and the dialog never scrolls as well.
      */}
      {selected?.debrief ? (
        <PracticeReport
          sessionId={selected.id}
          personaName={card.persona.name}
          debrief={selected.debrief}
          className='pb-1'
          fill
        />
      ) : (
        <div className='flex flex-1 flex-col gap-4 px-6.5 pt-5.5 pb-1'>
          {isPending && (
            <p className='text-base text-ink-muted'>{m.practice_feedback_loading()}</p>
          )}
          {isError && (
            <p role='alert' className='text-base text-danger'>
              {m.practice_feedback_failed()}
            </p>
          )}
          {!isPending && !isError && selected === undefined && (
            <p className='text-base text-ink-muted'>{m.practice_feedback_empty()}</p>
          )}
        </div>
      )}

      <DialogFooter className='gap-2.5 px-6.5 pt-4 pb-6.5'>
        <Button
          variant='quiet'
          shape='block'
          className='px-4 font-medium'
          onClick={onClose}
        >
          {m.practice_close()}
        </Button>
        <Button disabled={starting} onClick={onPractiseAgain}>
          <RotateCcw className='size-3.5' aria-hidden='true' />
          {m.practice_start_again()}
        </Button>
      </DialogFooter>
    </>
  );
}

export interface PracticeFeedbackDialogProps {
  /** The card whose feedback is showing. Null closes the dialog. */
  card: RoleplayCard | null;
  starting: boolean;
  onClose: () => void;
  onPractiseAgain: () => void;
}

/**
 * What Wundo made of your practice, attempt by attempt.
 *
 * Keyed on the card so opening another one starts on its newest attempt rather
 * than on whichever attempt the last card was left on.
 */
export function PracticeFeedbackDialog({
  card,
  starting,
  onClose,
  onPractiseAgain,
}: PracticeFeedbackDialogProps) {
  return (
    <Dialog open={card !== null} onOpenChange={(next) => !next && onClose()}>
      {/*
        Wider and less rounded than a decision dialog. This one is a report to
        be read — five criteria with a paragraph each — and at the standard
        width every line breaks twice. As tall as the window allows, because
        only the report inside it scrolls.
      */}
      <DialogContent size='full' className='max-w-190 overflow-hidden rounded-lg'>
        <DialogHeader className='gap-4 border-line px-6.5 py-5.5'>
          <div className='min-w-0'>
            <DialogTitle className='text-xl font-medium tracking-tight'>
              {m.practice_feedback_title()}
            </DialogTitle>
            {card !== null && (
              <DialogDescription className='mt-1.25 text-base font-medium text-ink-secondary'>
                {`${card.title} · ${card.persona.name}`}
              </DialogDescription>
            )}
          </div>
          <DialogClose
            aria-label={m.practice_close()}
            className='size-9 rounded-md bg-fill hover:bg-line'
          />
        </DialogHeader>

        {card !== null && (
          <PracticeFeedbackBody
            key={card.id}
            card={card}
            starting={starting}
            onClose={onClose}
            onPractiseAgain={onPractiseAgain}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
